import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from './Modal';
import {
  stageMeta, SCHOOL_STAGES, EXTERNAL_STAGES, SCHOOL_TRANSITIONS, EXTERNAL_TRANSITIONS,
} from '../../lib/stages';
import {
  transitionSchema as getSchema,
  validateTransitionPayload,
} from '../../lib/transition-schemas';
import ContactedForm from './transitions/ContactedForm';
import EngagedForm from './transitions/EngagedForm';
import VerbalYesForm from './transitions/VerbalYesForm';
import ContractedForm from './transitions/ContractedForm';
import DormantForm from './transitions/DormantForm';
import ClosedLostForm from './transitions/ClosedLostForm';
import MaintainedForm from './transitions/MaintainedForm';
import ProspectForm from './transitions/ProspectForm';

// Dispatcher to per-stage forms. Each transition captures stage-specific
// structured fields (channel/contact_person, response_type/next_action,
// confirmed_by/expected_signing_date, etc) which land in
// `interactions.structured_payload_json` for downstream analytics.
//
// `forceMode` allows transitions outside the canonical allowedTargets map.
// In force mode the dialog: (1) lets the user pick any stage, (2) requires an
// override_reason ≥ 10 chars, (3) submits force_stage=true so the server
// records forced_skip=1 with the reason in structured_payload.
//
// `defaultTarget` preselects the target stage on open — used by the kanban
// drop handler so the dialog opens with the right form already in view.

const FORM_BY_STAGE = {
  contacted: ContactedForm,
  engaged: EngagedForm,
  verbal_yes: VerbalYesForm,
  contracted: ContractedForm,
  dormant: DormantForm,
  closed_lost: ClosedLostForm,
  maintained: MaintainedForm,
  prospect: ProspectForm,
};

const MIN_FORCE_REASON_LEN = 10;

export default function StageTransitionDialog({
  open,
  account,
  initialTarget = '',
  onClose,
  onConfirm,
  forceMode = false,
  defaultTarget = '',
}) {
  const accountType = account?.type || 'school';
  const currentStage = account?.lifecycleStage || account?.lifecycle_stage || null;
  const stages = accountType === 'external' ? EXTERNAL_STAGES : SCHOOL_STAGES;
  const transitions = accountType === 'external' ? EXTERNAL_TRANSITIONS : SCHOOL_TRANSITIONS;
  const allowed = useMemo(() => transitions[currentStage] || [], [transitions, currentStage]);

  const [target, setTarget] = useState('');
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  // (Re)hydrate on open.
  useEffect(() => {
    if (!open) return;
    setTarget(defaultTarget || initialTarget || '');
    setValues({});
    setErrors(null);
    setOverrideReason('');
    setServerError('');
  }, [open, defaultTarget, initialTarget]);

  // Reset payload when target changes — different schemas have different
  // field sets and stale values would confuse validation.
  useEffect(() => { setValues({}); setErrors(null); }, [target]);

  const schema = getSchema(target);
  const FormComponent = FORM_BY_STAGE[target];
  const hasFields = !!FormComponent;

  const overrideOk = !forceMode || overrideReason.trim().length >= MIN_FORCE_REASON_LEN;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setServerError('');
    if (!target || target === currentStage) return;
    if (forceMode && !overrideOk) {
      setServerError(`Override reason must be at least ${MIN_FORCE_REASON_LEN} characters.`);
      return;
    }
    const v = validateTransitionPayload(target, values);
    if (v) { setErrors(v); return; }
    setSubmitting(true);
    try {
      // Keep `summary` inside structured_payload so the server-side schema
      // validation passes — several stages mark it required there. The same
      // value is also hoisted to the top-level `summary` field for the
      // interactions.summary column.
      const payload = { ...values };
      const summary = payload.summary || (schema?.summaryFromPayload?.(payload));
      const apiPayload = {
        to_stage: target,
        summary: forceMode ? `Force-stage override: ${overrideReason.trim()}` : summary,
        direction: 'outbound',
        structured_payload: payload,
      };
      if (forceMode) {
        apiPayload.force_stage = true;
        apiPayload.override_reason = overrideReason.trim();
      }
      await onConfirm(apiPayload);
      setTarget('');
      setValues({});
      setOverrideReason('');
      onClose?.();
    } catch (err) {
      setServerError(err?.data?.error || err?.message || 'Stage change failed');
    } finally {
      setSubmitting(false);
    }
  };

  const targetSelectable = stages.filter((s) => s !== currentStage);

  const titleVerb = forceMode ? 'Override stage' : 'Change stage';
  const submitLabel = forceMode ? 'Override' : 'Change stage';

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title={`${titleVerb}${currentStage ? ` from ${stageMeta(currentStage).label}` : ''}`}
      maxWidth="max-w-lg"
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <button type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">
            Cancel
          </button>
          <button type="submit" form="stage-transition-form"
            disabled={!target || target === currentStage || submitting || !overrideOk}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
              forceMode
                ? 'bg-red-500/90 text-white hover:bg-red-500'
                : 'bg-[#B0FC38] text-gray-900 hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20'
            }`}>
            {submitting ? (forceMode ? 'Overriding…' : 'Updating…') : submitLabel}
          </button>
        </div>
      }
    >
      <form id="stage-transition-form" onSubmit={handleSubmit} className="space-y-4">
        {forceMode && (
          <div className="flex gap-3 px-3 py-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/20 text-[12px] text-amber-300/90">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              This override is logged in the account's interaction timeline with your name and reason.
            </div>
          </div>
        )}

        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">Target stage</span>
          <select
            data-testid="stage-target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
          >
            <option value="">Select a stage…</option>
            {targetSelectable.map((s) => {
              const isAllowed = allowed.includes(s);
              const disabled = !forceMode && !isAllowed;
              return (
                <option key={s} value={s} disabled={disabled}>
                  {stageMeta(s).label}{!forceMode && !isAllowed ? ' (use force-stage)' : ''}
                </option>
              );
            })}
          </select>
        </label>

        {hasFields && (
          <div data-testid={`transition-form-${target}`}>
            <FormComponent values={values} onChange={setValues} errors={errors} />
          </div>
        )}

        {forceMode && (
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">
              Override reason (min {MIN_FORCE_REASON_LEN} chars) <span className="text-red-400/80">*</span>
            </span>
            <textarea
              data-testid="stage-override-reason"
              rows={3}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Why does the canonical path need to be skipped?"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
            />
          </label>
        )}

        {serverError && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {serverError}
          </div>
        )}
      </form>
    </Modal>
  );
}
